/**
 * Diagnose why the builder canvas is about to render NOTHING (DEV-17091,
 * hypothesis 2). The editor context's hydration silently bails when the
 * displayed version can't be resolved (`if (!current) return;`), and a
 * malformed graph blob hydrates to zero nodes — in both cases the canvas
 * used to render an empty grid indistinguishable from a genuinely empty
 * workflow, with no error surfaced anywhere. This helper names the cause so
 * the canvas can show it instead of a silent blank.
 *
 * Returns null when the canvas state is self-explanatory: still loading,
 * REVALIDATING (see below), a healthy graph, or a genuinely empty
 * (zero-node, well-formed, no-better-version-exists) workflow.
 * Pure so the node-env test suite covers every branch.
 */

/** What the classifier needs to know about each loaded version row. */
export interface IVersionSummary {
  id: string;
  versionNumber: number;
  /** Node count read off the version's graph blob; null when unreadable. */
  nodeCount: number | null;
}

export function diagnoseGraphHydration(args: {
  /** react-query status of the GET /v1/workflows/:id/versions request. */
  versionsStatus: 'pending' | 'error' | 'success';
  /**
   * True while that request is refetching in the background — ANY refetch,
   * including react-query's window-focus revalidation, not only the
   * post-save one. The motivating case is post-save: the workflow row
   * (with its fresh currentVersionId) lands BEFORE the versions list
   * refetch resolves, so for one round-trip the current version is
   * legitimately "missing from history". `status` stays 'success' through
   * a background refetch, so without this flag the classifier structurally
   * cannot tell revalidating from corrupt — and fired a false "data
   * inconsistency" banner on the happy path of every save. Side effect: a
   * genuinely corrupt workflow's banner blinks off during a focus refetch
   * and returns when it settles — accepted; a verdict computed over data
   * known to be mid-replacement isn't worth showing.
   */
  versionsFetching: boolean;
  /** Error message when versionsStatus === 'error'. */
  versionsErrorMessage?: string;
  /** Summaries of the loaded version rows, when success. */
  versions?: readonly IVersionSummary[];
  /** The workflow's currentVersionId (null = workflow has no version yet). */
  currentVersionId: string | null;
  /** Non-null when the user is viewing a specific historical version. */
  requestedVersionNumber?: number | null;
  /** The version the canvas resolved to display, or null. */
  displayedVersionGraph: unknown | undefined;
  /** True once a displayed version was resolved. */
  hasDisplayedVersion: boolean;
}): string | null {
  const {
    versionsStatus,
    versionsFetching,
    versionsErrorMessage,
    versions,
    currentVersionId,
    requestedVersionNumber,
    displayedVersionGraph,
    hasDisplayedVersion,
  } = args;

  if (versionsStatus === 'pending') return null;
  // Revalidation window: stale versions list + fresh workflow row. Any
  // verdict computed now is about data we already know is being replaced.
  if (versionsFetching) return null;

  if (versionsStatus === 'error') {
    return `Couldn't load this workflow's versions${
      versionsErrorMessage ? ` (${versionsErrorMessage})` : ''
    } — the canvas below is NOT the workflow's real graph. Reload to retry.`;
  }

  if (!hasDisplayedVersion) {
    if (requestedVersionNumber != null) {
      return `Version ${requestedVersionNumber} was not found in this workflow's version history — nothing to display.`;
    }
    if (currentVersionId === null) {
      // A workflow with no version at all — the empty canvas is truthful.
      return null;
    }
    const count = versions?.length ?? 0;
    return (
      `This workflow's current version (${currentVersionId}) is missing from its version ` +
      `history (${count} version${count === 1 ? '' : 's'} loaded) — the canvas cannot render ` +
      `the graph. This is a data inconsistency; please report it.`
    );
  }

  // A displayed version exists — flag a graph blob the hydrator can't read,
  // or one whose emptiness contradicts the rest of the stored data.
  const graph = displayedVersionGraph;
  if (graph == null || typeof graph !== 'object' || Array.isArray(graph)) {
    return 'This version stores a malformed graph snapshot — the canvas cannot render it. This is a data inconsistency; please report it.';
  }
  const { nodes, edges } = graph as { nodes?: unknown; edges?: unknown };
  if (nodes !== undefined && !Array.isArray(nodes)) {
    return 'This version stores a malformed graph snapshot (nodes is not a list) — the canvas cannot render it. This is a data inconsistency; please report it.';
  }
  // `{}` — no node list at all. Nothing in the product writes this shape
  // (the console's New Workflow posts explicit empty arrays), but bundle
  // import historically did, and it hydrates to a blank canvas that lies
  // about the workflow. Distinct message so reports can tell it apart.
  if (nodes === undefined) {
    return 'This version stores a graph snapshot with no node list at all — the canvas cannot render it. This is a data inconsistency; please report it.';
  }
  if (nodes.length === 0) {
    // Zero nodes but edges present — the stored graph disagrees with
    // itself. This is the blob/rows-divergence shape DEV-17091 describes.
    if (Array.isArray(edges) && edges.length > 0) {
      return (
        `This version stores 0 nodes but ${edges.length} edge${edges.length === 1 ? '' : 's'} — ` +
        'the graph snapshot disagrees with itself, so the canvas cannot render it. ' +
        'This is a data inconsistency; please report it.'
      );
    }
    // Zero nodes while a NEWER version holds a real graph — the classic
    // detached-draft symptom: edits were saved onto a version that never
    // became current. Point at the candidate instead of showing a blank
    // canvas that looks like lost work. Strictly NEWER, because "older
    // siblings have nodes" is what a deliberate clear-out looks like
    // (select-all → delete → save cuts a new, empty current version on top
    // of the populated history) — flagging that told users their
    // intentional delete "may be lost", the same class of false positive
    // the fetching guard exists to kill. Deliberately viewing an old empty
    // version isn't a draft situation either — skip before doing any work.
    if (requestedVersionNumber == null) {
      const currentVersionNumber =
        versions?.find((v) => v.id === currentVersionId)?.versionNumber ?? Number.POSITIVE_INFINITY;
      // NEWEST matching version first (then node count as a tiebreak for
      // equal numbers, which shouldn't happen): the message says the graph
      // "may have been saved to a version that never became current", and
      // the version that claim describes is the latest one — not whichever
      // historical version happens to hold the most nodes.
      const candidate = [...(versions ?? [])]
        .filter(
          (v) =>
            v.id !== currentVersionId &&
            v.versionNumber > currentVersionNumber &&
            (v.nodeCount ?? 0) > 0,
        )
        .sort(
          (a, b) => b.versionNumber - a.versionNumber || (b.nodeCount ?? 0) - (a.nodeCount ?? 0),
        )[0];
      if (candidate) {
        return (
          `This workflow's current version is empty, but version ${candidate.versionNumber} holds ` +
          `${candidate.nodeCount} node${candidate.nodeCount === 1 ? '' : 's'} — the graph may have ` +
          `been saved to a version that never became current. Open that version from the version ` +
          `history to recover it, or report this.`
        );
      }
    }
  }
  // A well-formed zero-node graph with no better version anywhere is a
  // legitimately empty workflow: no banner.
  return null;
}
